import { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Box, Button, Card, DataTable, DescriptionList, InlineStack, Link, Modal, Page, Pagination, Select, Text } from '@shopify/polaris';
import { ClipboardIcon, RefreshIcon } from '@shopify/polaris-icons';
import { getRequest, postRequest } from '../../../../shared/components/functions';
import Toasts from '../../../../shared/components/toasts/toasts';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';

/*
  Settings > AI connections: who is connected to the read-only MCP server (Claude, ChatGPT, ...), revoke a grant or all
  grants of a user, and the audit log of every tool call. AdminFull only (server: routes/mcp_admin.js).
  The switch and allow-list are environment variables on the server (server/mcp/README.md), shown here read-only.
*/

const STATUS_TONES = { ok: 'success', error: 'critical', denied: 'warning' };
const PAGE_SIZE = 50;

// McpToken / McpToolCall times are real UTC (SYSUTCDATETIME), so the browser's local time is correct here.
const pad = (n) => String(n).padStart(2, '0');
function formatMoment(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function timeAgo(value) {
  if (!value) return '';
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (!Number.isFinite(minutes)) return '';
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} days ago`;
}

// {"query":"Aha","limit":10} -> query: "Aha", limit: 10
function summarizeArguments(raw) {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return String(raw);
    const text = Object.entries(parsed)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');
    return text.length > 80 ? `${text.slice(0, 79)}…` : text;
  } catch (err) {
    return raw.length > 80 ? `${raw.slice(0, 79)}…` : raw;
  }
}

function prettyArguments(raw) {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch (err) {
    return raw || '';
  }
}

const userLabel = (row) => row.UserName || `User ${row.UserID}`;
const clientLabel = (row) => row.ClientName || row.ClientID || 'Unknown client';

function AllowList({ allowedUsers }) {
  if (allowedUsers === 'all') return <Text as="span">Everyone with an allowed role (MCP_ALLOWED_USER_IDS=*)</Text>;
  if (!Array.isArray(allowedUsers) || allowedUsers.length === 0) {
    return (
      <Text as="span" tone="critical">
        Nobody: MCP_ALLOWED_USER_IDS is empty
      </Text>
    );
  }
  return (
    <InlineStack gap="100" wrap>
      {allowedUsers.map((user) => (
        <Badge key={user.id} tone={user.issue ? 'warning' : undefined}>
          {`${user.name || `User ${user.id}`}${user.roleName ? ` (${user.roleName})` : ''}${user.issue ? `: ${user.issue}` : ''}`}
        </Badge>
      ))}
    </InlineStack>
  );
}

const CAPABILITY_SHORT = { purchase_prices: 'Purchase prices', company_figures: 'Company figures' };

// short badges (full label on hover); wraps inside DataTable cells, which do not wrap by default
const CapabilityBadges = ({ capabilities, empty = 'Records only' }) =>
  capabilities && capabilities.length ? (
    <div style={{ whiteSpace: 'normal' }}>
      <InlineStack gap="100" wrap>
        {capabilities.map((c) => (
          <span key={c.id} title={c.label}>
            <Badge tone="info">{CAPABILITY_SHORT[c.id] || c.label}</Badge>
          </span>
        ))}
      </InlineStack>
    </div>
  ) : (
    <Text as="span" tone="subdued">
      {empty}
    </Text>
  );

// Access tiers (server/mcp/policy.js): role defaults, the Entra app roles that grant more, and who has them
function TiersCard({ tiers }) {
  if (!tiers) return null;
  const roleNames = (list) => list.map((r) => <code key={r}>{r}</code>).reduce((acc, el, i) => (i ? [...acc, ', ', el] : [el]), []);
  return (
    <Card>
      <CardHeading title="Access tiers" />
      <BlockStack gap="300">
        <Text as="p" tone="subdued" variant="bodySm">
          Everyone who may connect reads records (customers, orders, invoices per customer, machines, stock, ...). Purchase prices / margins and company-wide totals need an extra tier. Payroll data
          and hidden companies are never available through the MCP.
        </Text>
        <DescriptionList
          spacing="tight"
          items={[
            ...tiers.roleDefaults.map((r) => ({ term: `${r.name} (default)`, description: <CapabilityBadges capabilities={r.capabilities} /> })),
            {
              term: 'Entra app roles',
              description: (
                <BlockStack gap="100">
                  <Text as="span">Access: {roleNames(tiers.accessRoles)} (instead of the allow-list)</Text>
                  <Text as="span">Purchase prices and margins: {roleNames(tiers.purchasePricesRoles)}</Text>
                  <Text as="span">Company figures: {roleNames(tiers.companyFiguresRoles)}</Text>
                  <Text as="span" tone="subdued" variant="bodySm">
                    {`Read from the SAML claim ${tiers.roleClaim} at every Microsoft login. Connecting asks for a fresh Microsoft login when the stored roles are older than ${tiers.entraMaxAgeHours} hours; a connection must sign in again after ${tiers.grantMaxDays} days.`}
                  </Text>
                </BlockStack>
              )
            },
            {
              term: 'Users with Entra roles',
              description: !tiers.entraTableReady ? (
                <Text as="span" tone="critical">
                  The McpUserEntraRoles table is missing: run server/sql/create_mcp_tables.sql again (safe to re-run).
                </Text>
              ) : tiers.users.length ? (
                <BlockStack gap="150">
                  {tiers.users.map((u) => (
                    <InlineStack key={u.id} gap="200" blockAlign="center" wrap>
                      <Text as="span" fontWeight="semibold">{`${u.name || `User ${u.id}`} (${u.roleName})`}</Text>
                      {u.mcpAccessRole ? <Badge tone="success">MCP access</Badge> : null}
                      <CapabilityBadges capabilities={u.capabilities} />
                      <Text as="span" tone="subdued" variant="bodySm">{`Entra: ${u.entraRoles.join(', ') || 'no roles'} · ${timeAgo(u.syncedAt)}`}</Text>
                    </InlineStack>
                  ))}
                </BlockStack>
              ) : (
                <Text as="span" tone="subdued">
                  Nobody has MCP roles in Entra yet (or nobody logged in since they were assigned).
                </Text>
              )
            }
          ]}
        />
      </BlockStack>
    </Card>
  );
}

function StatusCard({ status, connections }) {
  const [copied, setCopied] = useState(false);
  if (!status) return null;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(status.resource);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // clipboard not available (http, permissions): the URL is selectable
    }
  };

  const activity = status.activity;
  const connectedUsers = new Set(connections.map((row) => row.UserID)).size;

  return (
    <Card>
      <CardHeading title="Status" />
      <DescriptionList
        spacing="tight"
        items={[
          {
            term: 'MCP server',
            description: (
              <InlineStack gap="200">
                <Badge tone={status.enabled ? 'success' : undefined}>{status.enabled ? 'On' : 'Off (MCP_ENABLED is not true)'}</Badge>
                <Badge tone={status.tablesReady ? 'success' : 'critical'}>{status.tablesReady ? 'Database tables ready' : 'Database tables missing'}</Badge>
              </InlineStack>
            )
          },
          {
            term: 'Connector URL',
            description: (
              <InlineStack gap="200" blockAlign="center">
                <span style={{ fontFamily: 'monospace', userSelect: 'all' }}>{status.resource}</span>
                <Button size="micro" variant="tertiary" icon={ClipboardIcon} onClick={copyUrl} accessibilityLabel="Copy connector URL">
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </InlineStack>
            )
          },
          {
            term: 'Allowed roles',
            description: (
              <InlineStack gap="100">
                {status.allowedRoles.map((role) => (
                  <Badge key={role.id}>{role.name}</Badge>
                ))}
              </InlineStack>
            )
          },
          { term: 'Allow-list', description: <AllowList allowedUsers={status.allowedUsers} /> },
          {
            term: 'Sign-in',
            description: `Access tokens last ${Math.round(status.accessTokenTtlSeconds / 60)} minutes; a connection signs in with Microsoft again after ${
              status.tiers?.grantMaxDays || status.refreshTokenTtlDays
            } days. At most ${status.toolCallsPerMinute} tool calls per minute per user.`
          },
          {
            term: 'Usage',
            description: activity
              ? `${connections.length} active connection${connections.length === 1 ? '' : 's'} (${connectedUsers} user${connectedUsers === 1 ? '' : 's'}). Last ${activity.days} days: ${
                  activity.calls
                } tool call${activity.calls === 1 ? '' : 's'} by ${activity.users} user${activity.users === 1 ? '' : 's'}, ${activity.errors} failed, ${activity.denied} refused.`
              : 'Not available until the database tables exist.'
          }
        ]}
      />
      <Box paddingBlockStart="200">
        <Text as="p" tone="subdued" variant="bodySm">
          These settings are environment variables on the server (MCP_ENABLED, MCP_ALLOWED_USER_IDS, MCP_ALLOWED_ROLES, see server/mcp/README.md). Change them in DigitalOcean and redeploy.
        </Text>
      </Box>
    </Card>
  );
}

function ConnectionsCard({ connections, loading, onRevoke }) {
  const perUser = connections.reduce((acc, row) => ({ ...acc, [row.UserID]: (acc[row.UserID] || 0) + 1 }), {});

  const rows = connections.map((row) => [
    userLabel(row),
    <Text as="span">{clientLabel(row)}</Text>,
    formatMoment(row.ConnectedAt),
    row.LastUsedAt ? (
      <span title={formatMoment(row.LastUsedAt)}>{timeAgo(row.LastUsedAt)}</span>
    ) : (
      <Text as="span" tone="subdued">
        Not yet
      </Text>
    ),
    <CapabilityBadges capabilities={row.Capabilities} />,
    row.Calls30d,
    formatMoment(row.ExpiresAt),
    <InlineStack gap="200" wrap={false}>
      <Button size="slim" tone="critical" onClick={() => onRevoke({ kind: 'connection', row })}>
        Revoke
      </Button>
      {perUser[row.UserID] > 1 ? (
        <Button size="slim" variant="tertiary" tone="critical" onClick={() => onRevoke({ kind: 'user', row, count: perUser[row.UserID] })}>
          {`Revoke all ${perUser[row.UserID]}`}
        </Button>
      ) : null}
    </InlineStack>
  ]);

  return (
    <Card>
      <CardHeading title={`Active connections (${connections.length})`} />
      {!loading && connections.length === 0 ? (
        <Text as="p" tone="subdued">
          Nobody is connected. Users connect from Claude or ChatGPT with the connector URL above and sign in with their Microsoft account.
        </Text>
      ) : (
        <DataTable
          increasedTableDensity
          verticalAlign="middle"
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
          headings={[<b>User</b>, <b>Client</b>, <b>Connected since</b>, <b>Last used</b>, <b>Extra access</b>, <b>Calls (30 days)</b>, <b>Signed in until</b>, <b></b>]}
          rows={rows}
        />
      )}
    </Card>
  );
}

function ToolCallsCard({ filterOptions, filters, setFilters, page, setPage, result, loading, onShowCall }) {
  const userOptions = [{ label: 'All users', value: '' }, ...filterOptions.users.map((user) => ({ label: user.name || `User ${user.id}`, value: String(user.id) }))];
  const toolOptions = [{ label: 'All tools', value: '' }, ...filterOptions.tools.map((tool) => ({ label: tool, value: tool }))];
  const statusOptions = [
    { label: 'All results', value: '' },
    { label: 'OK', value: 'ok' },
    { label: 'Failed', value: 'error' },
    { label: 'Refused (rate limit)', value: 'denied' }
  ];
  const change = (key) => (value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  // DataTable cells do not wrap: the user/client and arguments cells opt in so the table fits the page
  const rows = result.rows.map((call) => [
    formatMoment(call.CreatedAt),
    <div style={{ whiteSpace: 'normal', minWidth: 140 }}>
      <Text as="span">{userLabel(call)}</Text>
      <Text as="p" tone="subdued" variant="bodySm">
        {clientLabel(call)}
      </Text>
    </div>,
    <span style={{ fontFamily: 'monospace' }}>{call.Tool}</span>,
    <div style={{ whiteSpace: 'normal', maxWidth: 280, wordBreak: 'break-word' }}>
      <Link monochrome removeUnderline onClick={() => onShowCall(call)}>
        {summarizeArguments(call.Arguments) || 'No arguments'}
      </Link>
    </div>,
    <Badge tone={STATUS_TONES[call.Status]}>{call.Status}</Badge>,
    call.DurationMs != null ? `${call.DurationMs} ms` : '',
    call.ResultCount != null ? call.ResultCount : ''
  ]);

  return (
    <Card>
      <CardHeading title="Tool calls" />
      <BlockStack gap="300">
        <InlineStack gap="300" wrap>
          <Box minWidth="200px">
            <Select label="User" labelInline options={userOptions} value={filters.userId} onChange={change('userId')} />
          </Box>
          <Box minWidth="200px">
            <Select label="Tool" labelInline options={toolOptions} value={filters.tool} onChange={change('tool')} />
          </Box>
          <Box minWidth="200px">
            <Select label="Result" labelInline options={statusOptions} value={filters.status} onChange={change('status')} />
          </Box>
        </InlineStack>
        {!loading && result.rows.length === 0 ? (
          <Text as="p" tone="subdued">
            No tool calls{filters.userId || filters.tool || filters.status ? ' match these filters' : ' yet'}.
          </Text>
        ) : (
          <DataTable
            increasedTableDensity
            verticalAlign="middle"
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric']}
            headings={[<b>Time</b>, <b>User</b>, <b>Tool</b>, <b>Arguments</b>, <b>Result</b>, <b>Duration</b>, <b>Rows</b>]}
            rows={rows}
          />
        )}
        <InlineStack align="center">
          <Pagination hasPrevious={page > 1} onPrevious={() => setPage(page - 1)} hasNext={result.hasMore} onNext={() => setPage(page + 1)} label={`Page ${page}`} />
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export const SettingsAiConnectionsPage = () => {
  const [status, setStatus] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ users: [], tools: [] });
  const [filters, setFilters] = useState({ userId: '', tool: '', status: '' });
  const [page, setPage] = useState(1);
  const [calls, setCalls] = useState({ rows: [], hasMore: false });
  const [loadingCalls, setLoadingCalls] = useState(true);
  const [pending, setPending] = useState(null); // { kind: 'connection' | 'user', row, count? }
  const [revoking, setRevoking] = useState(false);
  const [shownCall, setShownCall] = useState(null);
  const [toast, setToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [errorToast, setErrorToast] = useState(false);

  // getRequest returns the error body on 4xx/5xx, so every response is shape-checked
  const loadOverview = useCallback(async () => {
    setLoadingConnections(true);
    const [statusData, connectionRows, options] = await Promise.all([getRequest('/api/mcp-admin/status'), getRequest('/api/mcp-admin/connections'), getRequest('/api/mcp-admin/tool-calls/filters')]);
    setStatus(statusData && typeof statusData.enabled === 'boolean' ? statusData : null);
    setConnections(Array.isArray(connectionRows) ? connectionRows : []);
    if (options && Array.isArray(options.users) && Array.isArray(options.tools)) setFilterOptions(options);
    setLoadingConnections(false);
  }, []);

  const loadCalls = useCallback(async () => {
    setLoadingCalls(true);
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
    const data = await getRequest(`/api/mcp-admin/tool-calls?${params.toString()}`);
    setCalls(data && Array.isArray(data.rows) ? data : { rows: [], hasMore: false });
    setLoadingCalls(false);
  }, [filters, page]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadCalls();
  }, [loadCalls]);

  const revoke = async () => {
    if (!pending) return;
    const { kind, row } = pending;
    const url = kind === 'connection' ? `/api/mcp-admin/connections/${encodeURIComponent(row.FamilyID)}/revoke` : `/api/mcp-admin/users/${row.UserID}/revoke`;
    setRevoking(true);
    // postRequest never throws: undefined means the server refused (403/404/5xx)
    const result = await postRequest(url, {});
    setRevoking(false);
    setPending(null);
    if (result) {
      setToastContent(kind === 'connection' ? `${clientLabel(row)} disconnected for ${userLabel(row)}` : `All AI connections of ${userLabel(row)} revoked`);
      setToast(true);
    } else {
      setErrorToast(true);
    }
    await loadOverview();
  };

  const refresh = () => {
    loadOverview();
    loadCalls();
  };

  const pendingTitle = pending
    ? pending.kind === 'connection'
      ? `Revoke ${clientLabel(pending.row)} for ${userLabel(pending.row)}?`
      : `Revoke all ${pending.count} connections of ${userLabel(pending.row)}?`
    : '';

  return (
    <Page
      title="AI connections"
      subtitle="Claude, ChatGPT and other AI clients connected through the read-only MCP server"
      backAction={{ content: 'Settings', url: '/settings' }}
      secondaryActions={[{ content: 'Refresh', icon: RefreshIcon, onAction: refresh }]}
    >
      <Toasts toast={toast} setToast={setToast} errorToast={errorToast} setErrorToast={setErrorToast} toastContent={toastContent} />
      <BlockStack gap="400">
        {status && !status.tablesReady ? (
          <Banner title="The MCP is not set up in this database yet" tone="critical">
            <p>{status.migrationMessage || 'Run server/sql/create_mcp_tables.sql on this organisation database.'}</p>
          </Banner>
        ) : null}
        {status && !status.enabled ? (
          <Banner title="The MCP server is switched off" tone="warning">
            <p>AI clients cannot connect or use existing connections until MCP_ENABLED=true is set on the server.</p>
          </Banner>
        ) : null}
        {!status && !loadingConnections ? (
          <Banner title="The status could not be loaded" tone="critical">
            <p>This page is only available to full administrators.</p>
          </Banner>
        ) : null}

        <StatusCard status={status} connections={connections} />
        <TiersCard tiers={status?.tiers} />
        {status?.tablesReady ? (
          <>
            <ConnectionsCard connections={connections} loading={loadingConnections} onRevoke={setPending} />
            <ToolCallsCard filterOptions={filterOptions} filters={filters} setFilters={setFilters} page={page} setPage={setPage} result={calls} loading={loadingCalls} onShowCall={setShownCall} />
          </>
        ) : null}
      </BlockStack>

      <Modal
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        title={pendingTitle}
        primaryAction={{ content: 'Revoke', destructive: true, onAction: revoke, loading: revoking }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setPending(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p">
              {pending?.kind === 'user'
                ? `${userLabel(pending.row)} is signed out of every AI client immediately.`
                : `${clientLabel(pending?.row || {})} loses access immediately: its next question fails and ${userLabel(pending?.row || {})} has to connect again.`}
            </Text>
            <Text as="p" tone="subdued">
              They can sign in again as long as they are on the allow-list. To block someone for good, remove them from MCP_ALLOWED_USER_IDS on the server as well.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={Boolean(shownCall)}
        onClose={() => setShownCall(null)}
        title={shownCall ? `${shownCall.Tool} · ${formatMoment(shownCall.CreatedAt)}` : ''}
        secondaryActions={[{ content: 'Close', onAction: () => setShownCall(null) }]}
      >
        {shownCall ? (
          <Modal.Section>
            <BlockStack gap="300">
              <Text as="p">
                {`${userLabel(shownCall)} via ${clientLabel(shownCall)}: `}
                <Badge tone={STATUS_TONES[shownCall.Status]}>{shownCall.Status}</Badge>
                {shownCall.DurationMs != null ? ` in ${shownCall.DurationMs} ms` : ''}
                {shownCall.ResultCount != null ? `, ${shownCall.ResultCount} rows` : ''}
              </Text>
              {shownCall.Error ? (
                <Banner tone={shownCall.Status === 'denied' ? 'warning' : 'critical'}>
                  <p>{shownCall.Error}</p>
                </Banner>
              ) : null}
              <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>{prettyArguments(shownCall.Arguments) || 'No arguments'}</pre>
              </Box>
              <Text as="p" tone="subdued" variant="bodySm">
                Arguments are stored up to 4000 characters; results are never stored.
              </Text>
            </BlockStack>
          </Modal.Section>
        ) : null}
      </Modal>
    </Page>
  );
};

export default SettingsAiConnectionsPage;
